import { template as template_736aeef09700465697d574a1f42e17ba } from "@ember/template-compiler";
import { CLINICIAN_SEARCH_TIME_BUCKET_ORDER, CLINICIAN_SEARCH_WEEKDAY_ORDER, clinicianSearchTimeBucketLabelKey, clinicianSearchWeekdayLabelKey, facetOptionDisabled } from 'client-portal/utils/clinicianSearch/facet-catalog';
import { fn } from '@ember/helper';
import { parseCommaSeparatedInts } from 'client-portal/utils/clinicianSearch/comma-query-param';
import { service } from '@ember/service';
import { toggleSelection } from 'client-portal/utils/clinicianSearch/toggle-selection';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import RequestClinicianSearchFiltersFilterCheckboxLine from '../filters/filter-checkbox-line';
import RequestClinicianSearchFiltersPanel from '../filters/panel';
import styles from './availability.module.scss';
import t from 'ember-intl/helpers/t';
export default class RequestClinicianSearchFacetFiltersAvailability extends Component {
    @service
    intl;
    @service
    clinicianSearch;
    styles = styles;
    @tracked
    selectedWeekdays = [];
    @tracked
    selectedTimeBuckets = [];
    @tracked
    weekdayCountById = new Map();
    @tracked
    timeCountById = new Map();
    constructor(){
        super(...arguments);
        this.selectedWeekdays = parseCommaSeparatedInts(this.clinicianSearch.query.availableDays);
        this.selectedTimeBuckets = parseCommaSeparatedInts(this.clinicianSearch.query.availableTimes);
    }
    get filterOptionsLoadingAriaLabel() {
        return this.intl.t('booking.clinicianSearch.filterDetail.filterOptionsLoading');
    }
    get weekdaysGroupLabel() {
        return this.intl.t('booking.clinicianSearch.filterDetail.weekdaysHeading');
    }
    get timesGroupLabel() {
        return this.intl.t('booking.clinicianSearch.filterDetail.timesHeading');
    }
    checkboxAriaLabel = (option, groupLabel)=>(option ? `${option}, ${groupLabel}` : '');
    get weekdayRows() {
        return CLINICIAN_SEARCH_WEEKDAY_ORDER.map((weekdayId)=>({
                weekdayId,
                docCount: this.weekdayCountById.get(weekdayId) ?? 0
            }));
    }
    get timeRows() {
        return CLINICIAN_SEARCH_TIME_BUCKET_ORDER.map((timeBucketId)=>({
                timeBucketId,
                docCount: this.timeCountById.get(timeBucketId) ?? 0
            }));
    }
    isWeekdaySelected = (weekdayId)=>this.selectedWeekdays.includes(weekdayId);
    isTimeSelected = (timeBucketId)=>this.selectedTimeBuckets.includes(timeBucketId);
    refreshFacets = async ()=>{
        let baseRouteParams = this.clinicianSearch.query.toRouteParams();
        let weekdayMeta = this.clinicianSearch.sendAll.buildFacetsMeta({
            ...baseRouteParams,
            availableDays: null
        });
        let timeMeta = this.clinicianSearch.sendAll.buildFacetsMeta({
            ...baseRouteParams,
            availableTimes: null
        });
        this.weekdayCountById = new Map((weekdayMeta?.facets?.availableDays ?? []).map((bucket)=>[
                Number(bucket.key),
                bucket.count
            ]));
        this.timeCountById = new Map((timeMeta?.facets?.availableTimes ?? []).map((bucket)=>[
                Number(bucket.key),
                bucket.count
            ]));
    };
    weekdayLabel = (weekdayId)=>{
        let key = clinicianSearchWeekdayLabelKey(weekdayId);
        return key ? this.intl.t(key) : '';
    };
    timeLabel = (timeBucketId)=>{
        let key = clinicianSearchTimeBucketLabelKey(timeBucketId);
        return key ? this.intl.t(key) : '';
    };
    weekdayOptionDisabled = (bucket)=>facetOptionDisabled(bucket.docCount, this.isWeekdaySelected(bucket.weekdayId));
    timeOptionDisabled = (bucket)=>facetOptionDisabled(bucket.docCount, this.isTimeSelected(bucket.timeBucketId));
    disabledRowTitle = (isDisabled)=>isDisabled ? this.intl.t('booking.clinicianSearch.filterDetail.disabledFilterOption') : '';
    updateSelection = (key, value, checked)=>{
        this[key] = toggleSelection(this[key], value, checked);
    };
    toggleWeekday = (weekdayId, event)=>this.updateSelection('selectedWeekdays', weekdayId, event.target.checked);
    toggleTime = (timeBucketId, event)=>this.updateSelection('selectedTimeBuckets', timeBucketId, event.target.checked);
    apply = ()=>{
        this.clinicianSearch.query.applyFacetFilter('availability', this.selectedWeekdays, this.selectedTimeBuckets);
        this.clinicianSearch.mobile.backFromDetail();
    };
    static{
        template_736aeef09700465697d574a1f42e17ba(`
    <RequestClinicianSearchFiltersPanel
      @class={{this.styles.component}}
      @facetKey="availability"
      @isLoading={{this.clinicianSearch.sendAll.isLoading}}
      @loadingAriaLabel={{this.filterOptionsLoadingAriaLabel}}
      @onApply={{this.apply}}
      @onLoad={{this.refreshFacets}}
      data-test-clinician-search-filter-availability-options
    >
      <p class="filter-section-title">{{t
          "booking.clinicianSearch.filterDetail.availabilityPanelTitle"
        }}</p>
      <div class="body">
        <div
          class="filter-field-block"
          role="group"
          aria-labelledby="clinician-search-availability-weekdays-heading"
        >
          <p
            class="subsection-title visually-hidden"
            id="clinician-search-availability-weekdays-heading"
          >
            {{t "booking.clinicianSearch.filterDetail.weekdaysHeading"}}
          </p>
          <ul class="filter-checkbox-list" role="list">
            {{#each this.weekdayRows as |bucket|}}
              <RequestClinicianSearchFiltersFilterCheckboxLine
                @kind="weekday"
                @optionId={{bucket.weekdayId}}
                @ariaLabel={{this.checkboxAriaLabel
                  (this.weekdayLabel bucket.weekdayId)
                  this.weekdaysGroupLabel
                }}
                @docCount={{bucket.docCount}}
                @title={{this.disabledRowTitle (this.weekdayOptionDisabled bucket)}}
                @checked={{this.isWeekdaySelected bucket.weekdayId}}
                @disabled={{this.weekdayOptionDisabled bucket}}
                @onChange={{fn this.toggleWeekday bucket.weekdayId}}
              >
                {{this.weekdayLabel bucket.weekdayId}}
              </RequestClinicianSearchFiltersFilterCheckboxLine>
            {{/each}}
          </ul>
        </div>

        <div
          class="filter-field-block time-stack"
          role="group"
          aria-labelledby="clinician-search-availability-times-heading"
        >
          <p
            class="subsection-title visually-hidden"
            id="clinician-search-availability-times-heading"
          >
            {{t "booking.clinicianSearch.filterDetail.timesHeading"}}
          </p>
          <ul class="filter-checkbox-list" role="list">
            {{#each this.timeRows as |bucket|}}
              <RequestClinicianSearchFiltersFilterCheckboxLine
                @kind="time"
                @optionId={{bucket.timeBucketId}}
                @ariaLabel={{this.checkboxAriaLabel
                  (this.timeLabel bucket.timeBucketId)
                  this.timesGroupLabel
                }}
                @docCount={{bucket.docCount}}
                @title={{this.disabledRowTitle (this.timeOptionDisabled bucket)}}
                @checked={{this.isTimeSelected bucket.timeBucketId}}
                @disabled={{this.timeOptionDisabled bucket}}
                @onChange={{fn this.toggleTime bucket.timeBucketId}}
              >
                {{this.timeLabel bucket.timeBucketId}}
              </RequestClinicianSearchFiltersFilterCheckboxLine>
            {{/each}}
          </ul>
        </div>
      </div>
    </RequestClinicianSearchFiltersPanel>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
